<script setup lang="ts">
import ImageWidget from "@/components/ui/ImageWidget.vue";
import { useClipboard } from "@/services/clipboard";
import { useTemplateRef } from "vue";
import FloatingTooltip from "./FloatingTooltip.vue";
import SimpleButton from "./SimpleButton.vue";

const {
  mediaType,
  base64Src,
  alt = "",
  scale = 1,
} = defineProps<{
  mediaType: string;
  base64Src: string;
  alt?: string;
  scale?: number;
}>();

const clipboard = useClipboard();
const imageWidget = useTemplateRef<InstanceType<typeof ImageWidget>>("imageWidget");

async function copyImage() {
  if (imageWidget.value) {
    await clipboard.copyElementAsPng(imageWidget.value.$el, scale);
  }
}
</script>

<template>
  <div class="copyable-image-widget">
    <div class="copy-button">
      <FloatingTooltip text="Copy image to clipboard" strategy="absolute">
        <SimpleButton icon-left="icon-copy" kind="ghost" @click="copyImage" />
      </FloatingTooltip>
    </div>

    <ImageWidget ref="imageWidget" :mediaType="mediaType" :base64-src="base64Src" :alt="alt" />
  </div>
</template>

<style scoped>
.copyable-image-widget {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--numbers-8);
  border: solid var(--border-tertiary) var(--stroke-width-md);
  border-radius: var(--numbers-16);

  .copy-button {
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    opacity: 0;
    transition: opacity var(--animation-duration) var(--animation-easing);

    :deep(.skore-button) {
      border-top: 0;
      border-right: 0;
      border-bottom-right-radius: 0;
      border-top-left-radius: 0;
      border-top-right-radius: var(--numbers-16);
    }
  }

  &:hover {
    .copy-button {
      opacity: 1;
    }
  }
}
</style>
